import { createContext, useCallback, useContext, useEffect, useRef, useMemo, useState } from "react";
import { fetchPeriods, fetchPipeline, fetchCloseReadiness, startRun, fetchRun, fetchLatestRun, fetchRunDecisions } from "@/lib/api";

const RunContext = createContext(null);

export const useRun = () => useContext(RunContext);

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

export const RunProvider = ({ children }) => {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("2026-07");
  const [config, setConfig] = useState(null);
  const [error, setError] = useState(false);

  // Run state
  const [started, setStarted] = useState(false);
  const [complete, setComplete] = useState(false);
  const [runId, setRunId] = useState(null);
  const [runStatus, setRunStatus] = useState(null); // PROCESSING | REVIEW_READY | FAILED
  const [runError, setRunError] = useState(null);
  const [reviewCases, setReviewCases] = useState(null);
  const [runSummary, setRunSummary] = useState(null);
  const [currentStage, setCurrentStage] = useState(null); // { stage, label, detail }
  const [stageHistory, setStageHistory] = useState([]);   // [{ stage, label, detail, duration_s }]
  const [runWallClock, setRunWallClock] = useState(null);

  const [runDecisions, setRunDecisions] = useState({});
  const [decisionSummary, setDecisionSummary] = useState(null);
 
   const [readiness, setReadiness] = useState(null);

  const pollRef = useRef(null);
  const pollStartRef = useRef(null);
  const currentStageStartRef = useRef(null); // wall-clock ms when current stage began
  const lastStageRef = useRef(null);          // last stage id seen by poll

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshReadiness = useCallback(() => {
    fetchCloseReadiness(periodId)
      .then(setReadiness)
      .catch(() => setReadiness(null));
  }, [periodId]);

  const refreshDecisions = useCallback(async (rid) => {
    if (!rid) return;
    try {
      const data = await fetchRunDecisions(rid);
      const map = Object.fromEntries(data.decisions.map(d => [d.case_id, d]));
      setRunDecisions(map);
      setDecisionSummary(data.summary);
    } catch {
      setRunDecisions({});
      setDecisionSummary(null);
    }
  }, []);

  useEffect(() => {
    refreshReadiness();
  }, [refreshReadiness]);

  useEffect(() => {
    fetchPeriods().then(setPeriods).catch(() => setError(true));
  }, []);

  useEffect(() => {
    setConfig(null);
    setStarted(false);
    setComplete(false);
    setRunId(null);
    setRunStatus(null);
    setRunError(null);
    setReviewCases(null);
    setRunSummary(null);
    setCurrentStage(null);
    setStageHistory([]);
    setRunWallClock(null);
    setRunDecisions({});
    setDecisionSummary(null);
    stopPolling();
    fetchPipeline(periodId).then(setConfig).catch(() => setError(true));
    // Restore latest completed run for this period (survives page refresh / navigation)
    fetchLatestRun(periodId)
      .then((run) => {
        if (run?.status === "REVIEW_READY" && run.review_cases !== undefined) {
          setReviewCases(run.review_cases || []);
          setRunSummary(run.summary || null);
          setRunId(run.run_id);
          setRunStatus("REVIEW_READY");
          setComplete(true);
          setStarted(true);
          setCurrentStage({ stage: "DONE", label: "Complete", detail: "" });
          refreshDecisions(run.run_id);
        }
      })
      .catch(() => {}); // 404 = no completed run yet, nothing to restore
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId, stopPolling]);

  // Polling effect — runs while status is PROCESSING
  useEffect(() => {
    if (!runId || runStatus !== "PROCESSING") {
      stopPolling();
      return;
    }

    const poll = async () => {
      // Timeout guard — if Intugleflow never calls back, fail after POLL_TIMEOUT_MS
      if (pollStartRef.current && Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
        stopPolling();
        setRunStatus("FAILED");
        setRunError("No response received from Intugleflow after 30 minutes. Check the flow trace.");
        return;
      }

      try {
        const data = await fetchRun(runId);

        // Track stage transitions — record duration of each completed stage
        if (data.current_stage && data.current_stage !== lastStageRef.current) {
          const now = Date.now();
          // Record the previous stage as done with its duration
          if (lastStageRef.current && currentStageStartRef.current) {
            const duration_s = Math.round((now - currentStageStartRef.current) / 1000);
            setStageHistory((prev) => [
              ...prev.filter((s) => s.stage !== lastStageRef.current),
              {
                stage: lastStageRef.current,
                label: data.current_stage_label || lastStageRef.current,
                duration_s,
              },
            ]);
          }
          lastStageRef.current = data.current_stage;
          currentStageStartRef.current = now;
          setCurrentStage({
            stage: data.current_stage,
            label: data.current_stage_label || data.current_stage,
            detail: data.current_stage_detail || "",
          });
        } else if (data.current_stage && !lastStageRef.current) {
          // First stage seen — initialise the start time
          lastStageRef.current = data.current_stage;
          currentStageStartRef.current = Date.now();
          setCurrentStage({
            stage: data.current_stage,
            label: data.current_stage_label || data.current_stage,
            detail: data.current_stage_detail || "",
          });
        }

        if (data.status === "REVIEW_READY") {
          stopPolling();
          // Compute accurate per-stage durations from server-side stage_history timestamps.
          // This captures every stage including sub-second ones the 1s poll would miss.
          if (data.stage_history?.length > 0) {
            const hist = data.stage_history;
            const computed = hist.map((entry, i) => {
              const start = new Date(entry.arrived_at);
              const end =
                i + 1 < hist.length
                  ? new Date(hist[i + 1].arrived_at)
                  : new Date(data.updated_at);
              return {
                stage: entry.stage,
                label: entry.stage_label,
                detail: entry.detail || "",
                duration_s: Math.max(0, Math.round((end - start) / 1000)),
              };
            });
            setStageHistory(computed);
          }
          // Compute wall-clock total from run doc timestamps
          if (data.created_at && data.updated_at) {
            const total = Math.round(
              (new Date(data.updated_at) - new Date(data.created_at)) / 1000
            );
            setRunWallClock(total);
          }
          setRunStatus("REVIEW_READY");
          setReviewCases(data.review_cases || []);
          setRunSummary(data.summary || null);
          setCurrentStage({ stage: "DONE", label: "Complete", detail: "" });
          setComplete(true);
          refreshDecisions(data.run_id);
        } else if (data.status === "FAILED") {
          stopPolling();
          setRunStatus("FAILED");
          if (data.error && typeof data.error === "object") {
            setRunError(data.error);
          } else {
            setRunError({ message: data.error || "Reconciliation run failed." });
          }
        }
        // If still PROCESSING, keep polling
      } catch (err) {
        // Network hiccup — don't stop polling, just log
        console.warn("Poll error:", err);
      }
    };

    pollStartRef.current = Date.now();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return stopPolling;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId, runStatus, stopPolling]);

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  const start = useCallback(
    async (runParams) => {
      setComplete(false);
      setRunId(null);
      setRunStatus(null);
      setRunError(null);
      setReviewCases(null);
      setCurrentStage(null);
      setStageHistory([]);
      setRunWallClock(null);
      lastStageRef.current = null;
      currentStageStartRef.current = null;
      setStarted(true);

      try {
        const result = await startRun(runParams);
        setRunId(result.run_id);
        setRunStatus("PROCESSING");
      } catch (err) {
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to start reconciliation run.";
        setRunStatus("FAILED");
        setRunError(msg);
      }
    },
    []
  );

  const reset = useCallback(() => {
    stopPolling();
    setStarted(false);
    setComplete(false);
    setRunId(null);
    setRunStatus(null);
    setRunError(null);
    setReviewCases(null);
    setRunSummary(null);
    setCurrentStage(null);
    setStageHistory([]);
    setRunWallClock(null);
    lastStageRef.current = null;
    currentStageStartRef.current = null;
  }, [stopPolling]);

  // stageState is kept for the visual pipeline — all stages show "done" when REVIEW_READY
  const stageState = useCallback(
    (i) => {
      if (!started) return "pending";
      if (runStatus === "REVIEW_READY") return "done";
      if (runStatus === "FAILED") return i === 0 ? "done" : "pending";
      // PROCESSING: show first stage as running, rest as pending
      return i === 0 ? "running" : "pending";
    },
    [started, runStatus]
  );

  const navStatus = useMemo(
    () => ({
      close_run:
        complete || readiness?.close_approval ? "done" : started ? "active" : "idle",
      reconciliation:
        complete || readiness?.close_approval || readiness?.ready || reviewCases
          ? "active"
          : "locked",
      corrective_actions:
        decisionSummary?.corrective_actions_open > 0
          ? "active"
          : "locked",
      close_readiness: readiness?.close_approval
        ? "done"
        : readiness?.ready
        ? "active"
        : "locked",
    }),
    [started, complete, readiness, reviewCases, decisionSummary]
  );

  const value = {
    periods,
    periodId,
    setPeriodId,
    period: config?.period || periods.find((p) => p.id === periodId) || null,
    available: config?.available ?? null,
    config,
    error,
    started,
    complete,
    start,
    reset,
    stageState,
    navStatus,
    readiness,
    refreshReadiness,
    stages: config?.stages || [],
    summary: config?.summary || null,
    // Intugleflow run
    runId,
    runStatus,
    runError,
    reviewCases,
    runSummary,
    currentStage,
    stageHistory,
    runWallClock,
    // Decisions
    runDecisions,
    decisionSummary,
    refreshDecisions,
  };

  return <RunContext.Provider value={value}>{children}</RunContext.Provider>;
};
